import React from "react";
import { Check, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// RA note (2026-10) — Doc Control is a dot: green = all five complete, yellow = one or two missing,
// red = three or more missing. The five items (Drawings · Submittals · Shop · FRS · CO/RFI) are
// read-only — click the dot for the quick list, click anywhere outside to close it.
export const CHECKS = [["drawings", "Drawings"], ["submittals", "Submittals"], ["shop", "Shop drawings"], ["frs", "FRS"], ["co_rfi", "CO / RFI"]];
const DOT = { green: "bg-emerald-500", yellow: "bg-amber-400", red: "bg-red-500", grey: "bg-gray-300" };

export const docColour = (checks) => {
  if (!checks || !Object.keys(checks).length) return "grey";
  const missing = CHECKS.filter(([k]) => checks[k]?.ok !== true).length;
  return missing === 0 ? "green" : missing <= 2 ? "yellow" : "red";
};

export function DocChecks({ checks, testId, label = "" }) {
  const colour = docColour(checks);
  if (colour === "grey") return <span className="inline-block h-2.5 w-2.5 rounded-full bg-gray-300" data-testid={testId} data-colour="grey" />;
  const done = CHECKS.filter(([k]) => checks[k]?.ok === true).length;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()} data-testid={testId} data-colour={colour}
                title={`Doc Control · ${done} of ${CHECKS.length} complete — click for the list`}
                className={`inline-block h-2.5 w-2.5 rounded-full ${DOT[colour]} ring-offset-1 hover:ring-2 hover:ring-gray-300`} />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-3" data-testid={`${testId}-pop`} onClick={(e) => e.stopPropagation()}>
        <div className="text-xs font-semibold text-gray-900">Doc Control{label ? ` — ${label}` : ""} · {done} of {CHECKS.length}</div>
        <ul className="mt-2 space-y-1.5">
          {CHECKS.map(([k, name]) => {
            const ok = checks[k]?.ok === true;
            return (
              <li key={k} className="flex items-start gap-2 text-xs" data-testid={`${testId}-${k}`} data-ok={ok ? "1" : "0"}>
                {ok ? <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" strokeWidth={3} />
                    : <X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-600" strokeWidth={3} />}
                <span className={`w-24 shrink-0 font-medium ${ok ? "text-gray-700" : "text-red-700"}`}>{name}</span>
                <span className="whitespace-pre-wrap text-gray-600">{checks[k]?.detail || ""}</span>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

/** The job row: every live scope rolled up — complete only when every scope has it. */
export function rollupChecks(scopeRows) {
  const live = (scopeRows || []).filter((r) => r.checks && Object.keys(r.checks).length);
  if (!live.length) return null;
  const out = {};
  for (const [k] of CHECKS) {
    const bad = live.filter((r) => r.checks[k]?.ok !== true);
    out[k] = { ok: !bad.length,
               detail: bad.length ? bad.map((r) => `${r.scope}: ${r.checks[k]?.detail || "not complete"}`).join("\n") : "Complete on every scope" };
  }
  return out;
}
